import { useCallback, useContext, useLayoutEffect, useRef } from 'react';
import { useParams } from 'react-router';

import type { AxiosResponse } from 'axios';

import { KBApi } from '../api';
import { ErrorContext } from '../context/ErrorContext';
import { IsLoadingContext } from '../context/IsLoadingContext';
import { ViewContext } from '../context/ViewContext';
import { frontpageError } from './frontpage-error';

import type { FrontpageResponseData } from '../types';

// Loads the front page of the view in the route. A response for a view the
// reader has already navigated away from is dropped, so a slow earlier request
// cannot overwrite the current view.
export const useFetchFrontpage = () => {
  const params = useParams();
  const { setData } = useContext(ViewContext);
  const { setIsLoading } = useContext(IsLoadingContext);
  const { setError } = useContext(ErrorContext);

  const routeKey = params.hash;
  const currentRoute = useRef(routeKey);

  // The ref moves on as soon as the route does, before any effect runs.
  useLayoutEffect(() => {
    currentRoute.current = routeKey;
  }, [routeKey]);

  return useCallback(async () => {
    if (!routeKey) {
      setError('not_found');
      return;
    }
    setIsLoading(true);
    try {
      const response: AxiosResponse<FrontpageResponseData> = await KBApi.getFrontpage(routeKey);
      if (currentRoute.current !== routeKey) {
        return;
      }
      setData(response.data);
      setError(undefined);
    } catch (error) {
      if (currentRoute.current !== routeKey) {
        return;
      }
      console.error('Error fetching Frontpage', error);
      const failure = frontpageError(error);
      if (failure === 'not_found') {
        setData(null);
      }
      setError(failure);
    } finally {
      if (currentRoute.current === routeKey) {
        setIsLoading(false);
      }
    }
  }, [routeKey, setData, setIsLoading, setError]);
};
